export type SettingDataType = 'string' | 'integer' | 'boolean' | 'decimal' | 'json' | 'encrypted';

export type SettingCategory = 
  | 'payment' 
  | 'notification' 
  | 'email' 
  | 'app' 
  | 'business';

export interface SystemSetting {
  id: number;
  key: string;
  value: string | null;
  dataType: SettingDataType;
  category: SettingCategory;
  description: string | null;
  isSensitive: boolean;
  isRequired: boolean;
  isPublic: boolean;
  validationRules: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: number;
}

export interface SettingFormData {
  key: string;
  value: string;
  dataType: SettingDataType;
  category: SettingCategory;
  description?: string;
  isSensitive?: boolean;
  isRequired?: boolean;
  validationRules?: Record<string, unknown>;
}

export interface SettingsGrouped {
  [category: string]: SystemSetting[];
}

// Specific setting value types for better type safety
export interface PaymentSettings {
  razorpay_key_id?: string;
  razorpay_key_secret?: string;
  stripe_publishable_key?: string;
  stripe_secret_key?: string;
  paypal_client_id?: string;
  paypal_client_secret?: string;
}

export interface NotificationSettings {
  // FCM credentials are now stored in Edge Function secrets
  // This interface is kept for backward compatibility
  // but the actual credentials are managed via:
  // supabase secrets set FCM_SERVER_KEY=your_key
  // supabase secrets set FCM_SENDER_ID=your_sender_id
}

export interface EmailSettings {
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_password?: string;
  smtp_from_email?: string;
  smtp_from_name?: string;
}

export interface SMSSettings {
  twilio_account_sid?: string;
  twilio_auth_token?: string;
  twilio_phone_number?: string;
}

export interface StorageSettings {
  aws_access_key_id?: string;
  aws_secret_access_key?: string;
  aws_s3_bucket?: string;
  aws_s3_region?: string;
  cloudfront_distribution_id?: string;
}

export interface AnalyticsSettings {
  google_analytics_id?: string;
  mixpanel_token?: string;
  facebook_pixel_id?: string;
}

export interface AppSettings {
  app_name?: string;
  app_version_name?: string;
  app_version_code?: number;
  maintenance_mode?: boolean;
  maintenance_message?: string;
  max_file_upload_size?: number;
  session_timeout_minutes?: number;
  password_reset_expiry_hours?: number;
  beta_testing_enabled?: boolean;
  beta_testing_message?: string;
  beta_testing_end_date?: string;
}

export interface BusinessSettings {
  company_name?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  tax_rate?: number;
  referral_reward_amount?: number;
  referral_reward_currency?: string;
}

export interface ContentSettings {
  default_language?: string;
  supported_languages?: string[];
  content_moderation_enabled?: boolean;
  auto_publish_content?: boolean;
}

// Category metadata for UI organization
export interface SettingCategoryMeta {
  key: SettingCategory;
  title: string;
  description: string;
  icon: string;
  order: number;
}

export const SETTING_CATEGORIES: SettingCategoryMeta[] = [
  {
    key: 'app',
    title: 'Application',
    description: 'Core application settings and configuration',
    icon: 'Settings',
    order: 1,
  },
  {
    key: 'business',
    title: 'Business',
    description: 'Company information and business settings',
    icon: 'Building',
    order: 2,
  },
  {
    key: 'payment',
    title: 'Payment Gateways',
    description: 'Payment gateway configuration and API keys',
    icon: 'CreditCard',
    order: 3,
  },
  {
    key: 'notification',
    title: 'Push Notifications',
    description: 'Firebase configuration for push notifications',
    icon: 'Bell',
    order: 4,
  },
  {
    key: 'email',
    title: 'Email Settings',
    description: 'SMTP configuration for email delivery',
    icon: 'Mail',
    order: 5,
  },
];

// Validation schemas for different data types
export const VALIDATION_SCHEMAS = {
  email: {
    type: 'string',
    format: 'email',
    pattern: '^[\\w\\.-]+@[\\w\\.-]+\\.[a-zA-Z]{2,}$',
  },
  url: {
    type: 'string',
    format: 'uri',
    pattern: '^https?:\\/\\/.+',
  },
  phone: {
    type: 'string',
    pattern: '^\\+?[1-9]\\d{1,14}$',
  },
  port: {
    type: 'integer',
    minimum: 1,
    maximum: 65535,
  },
  percentage: {
    type: 'number',
    minimum: 0,
    maximum: 100,
  },
  currency: {
    type: 'string',
    pattern: '^[A-Z]{3}$',
  },
}; 